import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './Videos.css';

const DEFAULT_CHANNEL_URL = 'https://www.youtube.com/@direccionprovincialdelagua7694';

const formatPublishedAt = (value) => {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const Videos = () => {
  const [videos, setVideos] = useState([]);
  const [channelUrl, setChannelUrl] = useState(DEFAULT_CHANNEL_URL);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeVideoId, setActiveVideoId] = useState(null);

  useEffect(() => {
    let mounted = true;

    const fetchVideos = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/videos-youtube/`);

        if (!mounted) return;

        setVideos(response.data.results || []);
        setChannelUrl(response.data.channel_url || DEFAULT_CHANNEL_URL);
        setActiveVideoId(null);
        setError('');
      } catch (err) {
        if (!mounted) return;

        setVideos([]);
        setError('No se pudieron cargar los ultimos videos del canal.');
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchVideos();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (loading) {
      return undefined;
    }

    const targets = document.querySelectorAll('.videos-section .reveal-on-scroll:not(.visible)');
    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -25px 0px' }
    );

    targets.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, [loading, videos, error]);

  return (
    <section className="videos-section">
      <div className="home-container videos-container">
        <div className="videos-header reveal-on-scroll">
          <div>
            <p className="videos-kicker">YOUTUBE</p>
            <h2 className="videos-title">Videos del Canal</h2>
          </div>
          <a
            href={channelUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="videos-channel-link"
          >
            Ver canal completo
          </a>
        </div>

        {loading && (
          <div className="videos-feedback reveal-on-scroll">
            Cargando ultimos videos...
          </div>
        )}

        {!loading && error && (
          <div className="videos-feedback videos-feedback-error reveal-on-scroll">
            {error}
          </div>
        )}

        {!loading && !error && videos.length === 0 && (
          <div className="videos-feedback reveal-on-scroll">
            Todavia no hay videos para mostrar.
          </div>
        )}

        {!loading && videos.length > 0 && (
          <div className="videos-grid">
            {videos.map((video) => (
              <article key={video.id} className="video-card reveal-on-scroll">
                <div className="video-iframe-wrapper">
                  {activeVideoId === video.id ? (
                    <iframe
                      src={`${video.embed_url}?rel=0&modestbranding=1&autoplay=1`}
                      title={video.title}
                      loading="lazy"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  ) : (
                    <button
                      type="button"
                      className="video-thumbnail-button"
                      onClick={() => setActiveVideoId(video.id)}
                      aria-label={`Reproducir ${video.title}`}
                    >
                      <img
                        src={video.thumbnail_url}
                        alt={video.title}
                        className="video-thumbnail-image"
                      />
                      <span className="video-thumbnail-overlay">
                        <span className="video-thumbnail-play">Reproducir</span>
                      </span>
                    </button>
                  )}
                </div>
                <div className="video-card-body">
                  <h3 className="video-card-title">{video.title}</h3>
                  {video.published_at && (
                    <p className="video-card-date">
                      {formatPublishedAt(video.published_at)}
                    </p>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Videos;
